import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import Typography from '@mui/material/Typography/Typography'
import React, { useState } from 'react'
import { useShopCart } from 'src/Libs/shop/hooks'
import { Address } from 'src/Libs/shop/models'
import { AddressType } from '../../models/billling-and-shipping-address-modal.model'
import ShippingAddress from './ShippingAddress'
import { ShippingAddressForm } from '../../models/new-shipping-address-modal.model'
import { Button } from '@mui/material'

export interface BillingAndShippingAddressModalProps {
  addressType: AddressType
  defaultAddress: string | null
  onClose: () => void
  place: number
  addressList: Address[]
  onConfirm: (newAddress: string, onClose: () => void) => void
  onNewAddress: (onClose: () => void) => void
}

/*
 * This Component is used to select  Shipping or Billing Address (also New Address) on Shop Checkout
 */
const BillingAndShippingAddressModal: React.FC<BillingAndShippingAddressModalProps> = ({
  onClose,
  addressType,
  onConfirm,
  defaultAddress,
  onNewAddress,
  addressList,
  place,
}) => {
  const { t } = useCustomTranslation()
  const { saveTemporaryShippingAddress } = useShopCart()
  const [selectedAddress, setSelectedAddress] = useState<string | null>(null)

  const handleSelectedAddressChange = (address: string): void => {
    setSelectedAddress(address)
  }

  const updateTemporaryAddress = (): void => {
    const selectedShippingAddress = addressList.find(address => address.addressId === selectedAddress) ?? null
    if (addressType === AddressType.Shipping && selectedShippingAddress !== null && selectedAddress) {
      const addressInformation: ShippingAddressForm = {
        street: selectedShippingAddress.street,
        zipCode: selectedShippingAddress.zipCode,
        city: selectedShippingAddress.city,
        countryIso2: selectedShippingAddress.countryIso2,
        companyName: selectedShippingAddress.companyName,
        companyNameAddition: selectedShippingAddress.companyNameAddition,
      }
      saveTemporaryShippingAddress(addressInformation).then(() => {
        onConfirm(selectedAddress, onClose)
      })
    }
    if (addressType === AddressType.Billing && selectedAddress) {
      onConfirm(selectedAddress, onClose)
    }
  }

  const modalActions = {
    primary: {
      name: t('common.save'),
      onClick: () => {
        updateTemporaryAddress()
        onClose()
      },
    },
    secondary: {
      name: t('common.cancel'),
      onClick: () => {
        onClose()
      },
    },
  }

  return (
    <Dialog title={t('shop.billing-and-shipping.hint')} onClose={onClose} actions={modalActions} maxWidth="sm" place={place}>
      {addressType === AddressType.Billing ? (
        <>
          <Typography sx={{ mb: 2 }} variant="h4">
            {t('shop.billing.title')}
          </Typography>
          <ShippingAddress
            availableAddresses={addressList}
            handleSelected={handleSelectedAddressChange}
            selected={selectedAddress !== null ? selectedAddress : defaultAddress}
          />
        </>
      ) : (
        <>
          <Typography sx={{ mb: 2 }} variant="h4">
            {t('shop.shipping.title')}
          </Typography>
          <ShippingAddress
            availableAddresses={addressList}
            handleSelected={handleSelectedAddressChange}
            selected={selectedAddress !== null ? selectedAddress : defaultAddress}
          />
          <Button
            onClick={() => {
              onNewAddress(onClose)
            }}
            variant="contained"
            sx={{ marginTop: 2 }}
          >
            {t('shop.shipping.new-address')}
          </Button>
        </>
      )}
    </Dialog>
  )
}

export default BillingAndShippingAddressModal
